import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { AppService } from '@app/services/app/app.service';
import { UrlService } from '@core/services/url/url.service';
import { FileDownloadService } from '@core/services/download/file-download.service';
import { VersionsEditorService, VersionsViewService } from '@app/services/app/versions.service';
import { AnnotationsService } from '@core/services/annotations/annotations.service';
import { VersionMessageContext } from '@core/constants/version-message-context.constants';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';

import * as flContexts from '@core/constants/files-list-contexts.constants';

import * as Objects from '@core/objects';
import { AccessVersion } from '@app/models/access';

@Component({
    selector: 'version-details-content-messages',
    templateUrl: './messages.component.html',
    styleUrls: ['./messages.component.scss']
})
export class VersionsDetailsContentMessagesComponent implements OnInit, OnDestroy {
    // msg available contexts: used to select and filter the messages to display
    public readonly VERSION_MESSAGE_CONTEXT = VersionMessageContext;

    /* 15-12-2022 ==> version messages component no longer have feedback meesages or approval info inside. */

    @Input() version: Objects.Version;

    public access: AccessVersion;
    public openAttachmentsMessageIds: Set<string> = new Set<string>();
    public user: Objects.User = null;
    public versionMessages: Array<Objects.VersionMessage> = null;
    public prefixUrl: string = '';
    public messageAddUrl: string = '';

    // Notes management
    public notesCounterMap: { [messageId: number]: Objects.AnnotationsCounter[] } = {};
    public loadingNotesMap: { [messageId: number]: boolean } = {};
    public scope: string = flContexts.CTX.APP_ARTWORK_DETAILS_FEEDBACK;

    // File menu for signed files list
    public fileMenu: Array<ThreeDotsMenuItem> = [
        {
            title: "Download File",
            key: "download", // included in SIGNATURE_DEPENDENT_MENU_ACTIONS to check signature before download
            icon: "download"
        }
    ];

    protected messagesSubscriptor: Subscription;
    protected appSubscriptor: Subscription;
    protected accessSubscriptor: Subscription;  
    protected urlSubscriptor: Subscription; 
    
    constructor(
        public appSrv: AppService,
        private versionsViewSrv: VersionsViewService, 
        private urlSrv: UrlService,
        private fileDownloadSrv: FileDownloadService,
        private activeRoute: ActivatedRoute,
        private editorVersionSrv: VersionsEditorService,
        private annotationsSrv: AnnotationsService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit() {
        // When go from version to version (since jan-2024) get the version id and if changed clear messages.
        this.urlSubscriptor = this.activeRoute.params.subscribe((params: any) => {
            // Get the 'vid' parameter from the route and cast to number
            const routeVid = params.vid as number;
            if(routeVid){
                if(this.version.id && routeVid != this.version.id){
                    this.versionMessages = null;
                }
            }
        });

        this.prefixUrl = "/versions/"+ this.version.id;
        this.messageAddUrl = this.urlSrv.build(this.prefixUrl + "/messages");

        this.appSubscriptor = this.appSrv.getSession().subscribe(session => this.user = session.current.user);

        this.messagesSubscriptor = this.versionsViewSrv.selectComments().subscribe((messages: Array<Objects.VersionMessage>) => {
            // Here we filter the messages by context to get the real version messages only 
            if(messages != null){
                this.versionMessages = messages.filter((m:Objects.VersionMessage) => m.context == this.VERSION_MESSAGE_CONTEXT.DEFAULT);
                // Load notes for all messages
                this.versionMessages.forEach(msg => this.loadNotesForMessage(msg.message));
            }
        });

        this.accessSubscriptor = this.editorVersionSrv.selectAccessAll().subscribe((access: AccessVersion)=> this.access = access);
    }

    ngOnDestroy() {
        this.appSubscriptor.unsubscribe();
        this.messagesSubscriptor.unsubscribe();
        this.accessSubscriptor.unsubscribe();
        this.urlSubscriptor.unsubscribe();
    }

    onUpdateMessages(message: Objects.Message) {
        this.versionsViewSrv.loadComments();
    }

    onDeleteMessage(message: Objects.Message) {
        this.versionsViewSrv.loadComments();
    }

    toggleAttachments(message: any, itemKey?: string | number): void {
        if (!itemKey && (!message || typeof message !== 'object' || !message.id)) {
            return;
        }

        const messageKey = itemKey != null ? String(itemKey) : String(message.id);

        if (this.openAttachmentsMessageIds.has(messageKey)) {
            this.openAttachmentsMessageIds.delete(messageKey);
        } else {
            this.openAttachmentsMessageIds.add(messageKey);
        }
    }

    isAttachmentsOpen(message: any, itemKey?: string | number): boolean {
        const messageKey = itemKey != null ? String(itemKey) : (message && typeof message === 'object' && message.id ? String(message.id) : '');
        return !!messageKey && this.openAttachmentsMessageIds.has(messageKey);
    }

    /**
     * Load annotation counter for a specific message
     */
    private loadNotesForMessage(message: Objects.Message): void {
        if (!message || !message.id || !(message.attachments?.length > 0 || message.files?.length > 0)) {
            return;
        }

        // Skip if already loading or already loaded
        if (this.loadingNotesMap[message.id]) {
            return;
        }

        const counterUrlPrefix = '/messages/' + message.id;
        this.loadingNotesMap[message.id] = true;

        this.annotationsSrv.getCounter(counterUrlPrefix).subscribe({
            next: (counter: Objects.AnnotationsCounter[]) => {
                this.notesCounterMap[message.id] = [...counter];
                this.loadingNotesMap[message.id] = false;
                this.cdr.detectChanges();
            },
            error: (error: HttpErrorResponse) => {
                this.notesCounterMap[message.id] = [];
                this.loadingNotesMap[message.id] = false;
            }
        });
    }

    /**
     * Get notes for a specific message
     */
    getNotesForMessage(message: Objects.Message): Objects.AnnotationsCounter[] {
        return message && message.id ? (this.notesCounterMap[message.id] || []) : [];
    }

    /**
     * Handle file menu click from core-signed-files-list (download/notes)
     */
    onFileMenuClick(menuItem: AmsFilesListItemMenuClick): void {
        switch (menuItem.key) {
            case 'download':
                this.fileDownloadSrv.downloadFile(menuItem.file);
                break;
        }
    }

    /**
     * Handle file icon download click from core-signed-files-list
     */
    onFileIconDownloadClick(file: Objects.AmsFile): void {
        this.fileDownloadSrv.downloadFile(file);
    }

    /**
     * Handle file signature refresh from core-signed-files-list component
     * Reload all message comments to get fresh file signatures
     */
    onRefreshSignaturesNeeded(): void {
        this.versionsViewSrv.loadComments();
    }

    /**
     * TrackBy function for messages list
     */
    trackByMessageId(index: number, msg: any): string {
        if (!msg) {
            return '';
        }

        return msg.message?.id ? String(msg.message.id) : (msg.id ? String(msg.id) : '');
    }

    /**
     * TrackBy function for files list
     */
    trackByFileId(index: number, file: any): string {
        return file.uuid;
    }

    /**
     * Handle notes saved event from core-pdf-annotations-viewer to reload counter after saving notes.
     */
    notesSaved(message: Objects.Message, event: any) {
        event.stopPropagation();
        // After saving a note, refresh the notes counter for this message
        this.loadNotesForMessage(message);
    }

}
